import { ChatMessage, StoredChat } from '../../types/Chat';
import { splitAssistantContent } from '../../utils/SplitAssistantContent';

const DB_NAME = 'ChatsDatabase';
const STORE_NAME = 'chats';
const DB_VERSION = 1;

let db: IDBDatabase | null = null;

function normalizeAssistantMessage(
  msg: ChatMessage,
): ChatMessage & { _documentRaw?: string | null; _textPlain?: string } {
  if (msg.role !== 'assistant') return msg;

  const { textPlain, documentRaw } = splitAssistantContent(msg.content);

  if (documentRaw) {
    return {
      ...msg,
      _textPlain: textPlain,
      _documentRaw: documentRaw,
    };
  }

  return msg;
}

function normalizeMessagesForStorage(
  messages: ChatMessage[],
): (ChatMessage & { _documentRaw?: string | null; _textPlain?: string })[] {
  return messages.map(m => normalizeAssistantMessage(m));
}

function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (db) return resolve(db);

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject('Erro ao abrir o IndexedDB');

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = event => {
      const database = (event.target as IDBOpenDBRequest).result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export function deleteChatsDatabase(): Promise<void> {
  if (db) {
    try {
      db.close();
    } catch (e) {
      console.error(e);
    } finally {
      db = null;
    }
  }

  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function saveChat(chat: StoredChat): Promise<void> {
  if (!chat?.id || typeof chat.id !== 'string') {
    return;
  }

  const database = await initDB();

  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const payload: StoredChat = {
      ...chat,
      chat: {
        ...chat.chat,
        messages: normalizeMessagesForStorage(chat.chat?.messages ?? []),
      },
    };

    const request = store.put(payload);

    request.onsuccess = () => resolve();
    request.onerror = () => {
      reject('Erro ao salvar chat');
    };
  });
}

export async function getChatById(id: string): Promise<StoredChat | undefined> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject('Erro ao buscar chat');
  });
}

export async function getAllChats(): Promise<StoredChat[]> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject('Erro ao listar chats');
  });
}

export async function deleteChat(id: string): Promise<void> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject('Erro ao excluir chat');
  });
}
export async function updateChatWithMessages(
  chatId: string,
  newMessages: ChatMessage[],
): Promise<void> {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  const getRequest = store.get(chatId);

  getRequest.onsuccess = () => {
    const existing = getRequest.result;

    if (!existing) {
      return;
    }

    const normalizedIncoming = normalizeMessagesForStorage(newMessages);

    const mergedMessages = [
      ...(existing.chat.messages || []),
      ...normalizedIncoming,
    ];

    const updated = {
      id: chatId,
      chat: {
        createdAt: existing.chat.createdAt,
        messages: mergedMessages,
      },
    };

    const putRequest = store.put(updated);

    putRequest.onerror = event => {};
  };

  getRequest.onerror = () => {};
}
